import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { SortOrder } from "@/types/gql/graphql";
import { findObject } from "@/helper/array";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Pagination } from "./Pagination";
import { cn } from "@/lib/utils";
import { SearchInput } from "../form-control/search-input";
import { FilterField } from "./filters/FilterField";
import { FilterTable } from "./FilterTable";
import { SmallTableSkeleton } from "@/components/data-table/SmallTableSkeleton";
import { DataTableProps } from "@/components/data-table/types/universal-data-table-props.type";

export function DataTable<TData, TValue>({
  columns,
  data,
  onRowClick,
  sortOrders,
  onSortingChange,
  totalPage,
  currentPage,
  onChangePage,
  placeHolder = "Rechercher par plaque d’immatriculation",
  searchString,
  onSearchChange,
  title,
  filters,
  onFilter,
  isLoading = false,
}: Readonly<DataTableProps<TData, TValue>>) {
  // Convert sortOrders to TanStack Table sorting format
  const [sorting, setSorting] = useState<SortingState>(() => {
    if (sortOrders) {
      return [
        {
          id: sortOrders.sortField as string,
          desc: sortOrders.sortOrder === SortOrder.Desc,
        },
      ];
    }
    return [];
  });

  // Handle sorting changes and convert back to sortOrders format
  const handleSortingChange = (updaterOrValue: SortingState | ((old: SortingState) => SortingState)) => {
    setSorting((prevSorting) => {
      const newSorting = typeof updaterOrValue === "function" ? updaterOrValue(prevSorting) : updaterOrValue;

      if (onSortingChange) {
        if (newSorting.length > 0) {
          const firstSort = newSorting[0];
          onSortingChange({
            sortField: firstSort.id as keyof TData,
            sortOrder: firstSort.desc ? SortOrder.Desc : SortOrder.Asc,
          });
        } else {
          onSortingChange(undefined);
        }
      }

      return newSorting;
    });
  };

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onSortingChange: handleSortingChange,
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting,
    },
    manualPagination: true,
  });

  const handleFilterChange = (fieldName: string, selected: string[]) => {
    if (!filters) {
      return;
    }

    const newFilter = [...filters];
    const findField = findObject(filters, "value", fieldName);
    if (findField > -1) {
      newFilter[findField] = {
        ...newFilter[findField],
        selected: selected,
      };
      onFilter?.(newFilter);
    }
  };

  const hasFiltered = useMemo(() => {
    return filters?.some((filter) => filter.isShow);
  }, [filters]);

  return (
    <>
      <div className="flex justify-between mb-8">
        <span className="text-primary font-black text-xs">{title}</span>
        <div className="flex items-center gap-6">
          {(filters?.length ?? 0) > 0 && onFilter && <FilterTable filters={filters ?? []} onCheckedChange={onFilter} />}
          {onSearchChange && (
            <SearchInput
              placeholder={placeHolder}
              value={searchString}
              onChange={(event) => onSearchChange(event.target.value)}
            />
          )}
        </div>
      </div>
      {hasFiltered && filters?.length && (
        <div className="flex gap-4 pb-[27px]">
          {filters?.map((item) =>
            item.isShow ? (
              <FilterField
                key={`selected-table-${item.value}`}
                item={item.item}
                onChangePage={(selected) => handleFilterChange(item.value, selected)}
                selected={item.selected}
                label={item.label}
              />
            ) : null,
          )}
        </div>
      )}
      {isLoading ? (
        <SmallTableSkeleton />
      ) : (
        <>
          <div className="rounded-[15px] border overflow-hidden flex flex-col flex-1">
            <Table>
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id} className="h-13">
                    {headerGroup.headers.map((header) => (
                      <TableHead key={header.id}>
                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows?.length ? (
                  table.getRowModel().rows.map((row, idx) => (
                    <TableRow
                      key={row.id}
                      data-state={row.getIsSelected() && "selected"}
                      className={cn(
                        "data-[state=selected]:bg-gray-200",
                        idx % 2 === 0 ? "bg-[#E8E8E84D]" : "bg-white",
                        onRowClick && "cursor-pointer",
                      )}
                      onClick={() => onRowClick?.(row.original)}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id} className="h-13">
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={columns.length} className="h-24 text-center">
                      No results.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          {totalPage > 1 && (
            <div className="flex flex-col items-end m-4">
              <Pagination totalPage={totalPage} currentPage={currentPage.pageIndex} onChangePage={onChangePage} />
            </div>
          )}
        </>
      )}
    </>
  );
}
